// src/services/UserService.js
import { db, storage, auth } from '../firebase';
import { doc, updateDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Definimos los enums y estructuras necesarias
const SignInProvider = {
  EMAIL_LINK: 0,
  GOOGLE: 1,
  APPLE: 2,

  toString: function(value) {
    switch(value) {
      case this.GOOGLE: return "Google";
      case this.APPLE: return "Apple";
      case this.EMAIL_LINK: return "Email";
      default: return "Unknown";
    }
  }
};

class FolderModel {
  constructor(id, name, parentName = null) {
    this.id = id;
    this.name = name;
    this.parentName = parentName;
  }
}

class UserContact {
  constructor(name, email, sharesCount = 0) {
    this.id = crypto.randomUUID();
    this.name = name;
    this.email = email;
    this.sharesCount = sharesCount;
  }
}

const USER_CACHE_PREFIX = 'notemail:userCache:';

function normalizeFolderCounts(folderCounts) {
  const rawCounts = folderCounts && typeof folderCounts === 'object' ? folderCounts : {};
  const rawByFolderId = rawCounts.byFolderId && typeof rawCounts.byFolderId === 'object'
    ? rawCounts.byFolderId
    : {};

  return {
    inbox: Number.isFinite(Number(rawCounts.inbox)) ? Number(rawCounts.inbox) : 0,
    byFolderId: Object.entries(rawByFolderId).reduce((result, [folderId, count]) => {
      const numericCount = Number(count);
      if (Number.isFinite(numericCount) && numericCount > 0) {
        result[folderId] = numericCount;
      }
      return result;
    }, {}),
    version: Number.isFinite(Number(rawCounts.version)) ? Number(rawCounts.version) : 1,
    updatedAt: rawCounts.updatedAt || null
  };
}

function getUserCacheKey(uid) {
  return `${USER_CACHE_PREFIX}${uid}`;
}

// Supported locales for summarization language
export const SUPPORTED_LOCALES = [
  { code: "es", name: "Español" },
  { code: "en", name: "English" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "fr", name: "Français" },
  { code: "pt", name: "Português" },
  { code: "ca", name: "Català" },
];

// Summary style types
export const SUMMARY_TYPES = {
  standard: {
    value: "standard",
    label: "Smart",
    description: "The most balanced summary, focusing on the important topics and presented in a structured and organized way."
  },
  long: {
    value: "long",
    label: "Article", 
    description: "Structured in text sections like an article, it has a low level of summarization."
  },
  cleanAndFormat: {
    value: "cleanAndFormat",
    label: "Literal",
    description: "Not summarized; just presents the entire transcribed note in a clean and typo-corrected way."
  }
};

// Edition display configuration
export const EDITION_CONFIG = {
  free: { label: "Free", color: "bg-gray-500", textColor: "text-gray-700" },
  pro: { label: "PRO", color: "bg-orange-500", textColor: "text-orange-700" },
  founder: { label: "FOUNDER", color: "bg-yellow-500", textColor: "text-yellow-700" }
};

class UserService {
  constructor() {
    this._currentUser = null;
    this._listeners = new Set();
  }

  get currentUser() {
    return this._currentUser;
  }

  getCachedUserData(uid) {
    if (!uid) return null;

    try {
      const rawCache = localStorage.getItem(getUserCacheKey(uid));
      return rawCache ? JSON.parse(rawCache) : null;
    } catch (error) {
      console.warn('Error loading cached user data:', error);
      return null;
    }
  }

  setUser(firebaseUser, userData, options = {}) {
    const { persistCache = true } = options;
    this._currentUser = {
      // Datos de Firebase Auth
      email: firebaseUser?.email || null,
      uid: firebaseUser?.uid || null,
      photoURL: firebaseUser?.photoURL || null,
      
      // displayName viene de Firestore, no de Firebase Auth
      displayName: userData?.displayName || firebaseUser?.displayName || null,

      // Datos adicionales de Firestore
      sendEmailAddress: userData?.sendEmailAddress || null,
      secondarySendEmails: userData?.secondarySendEmails || [],
      hintKeywords: userData?.hintKeywords || [],
      maxNoteLengthSec: userData?.maxNoteLengthSec || 300,
      fcmTokens: userData?.fcmTokens || {},
      fcmTokenLocalDevice: userData?.fcmTokenLocalDevice || null,
      aboutMe: userData?.aboutMe || '',
      userLocale: userData?.userLocale || 'en',
      appInterfaceLanguageLocale: userData?.appInterfaceLanguageLocale || navigator.language,
      signInProvider: userData?.signInProvider || null,
      
      // Summary and email settings
      defaultSummaryType: userData?.defaultSummaryType || 'standard',
      defaultSendEmail: userData?.defaultSendEmail ?? true,
      addTranscriptInEmail: userData?.addTranscriptInEmail ?? false,
      
      // Subscription info
      edition: userData?.edition || 'free',
      subscriptionInfo: userData?.subscriptionInfo || null,
      
      // Statistics (read-only)
      notesCreated: userData?.notesCreated || 0,
      lastNoteCreatedDate: userData?.lastNoteCreatedDate || null,
      folderCounts: normalizeFolderCounts(userData?.folderCounts),
      
      // Webhook configuration
      webhook: userData?.webhook || {
        enabled: false,
        principalWebhook: '',
        executions: 0,
        errors: 0,
        lastError: null,
        lastExecutionDate: null,
        lastExecutionResult: null
      },
      
      // Migrations
      migrations: userData?.migrations || {},

      // Arrays de objetos
      folders: (userData?.folders || []).map(f => new FolderModel(f.id, f.name, f.parentName)),
      shareContacts: (userData?.shareContacts || []).map(c => new UserContact(c.name, c.email, c.sharesCount)),
      teamContacts: (userData?.teamContacts || []).map(c => new UserContact(c.name, c.email, c.sharesCount))
    };

    if (persistCache && this._currentUser.uid) {
      this._saveCachedUserData(this._currentUser);
    }

    this._notifyListeners();
  }

  clearUser() {
    this._currentUser = null;
    this._notifyListeners();
  }

  // Método para suscribirse a cambios
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  _notifyListeners() {
    this._listeners.forEach(listener => listener(this._currentUser));
  }

  _saveCachedUserData(userData) {
    try {
      const cacheData = {
        displayName: userData.displayName,
        folderCounts: userData.folderCounts,
        folders: (userData.folders || []).map(folder => ({
          id: folder.id,
          name: folder.name,
          parentName: folder.parentName || null
        }))
      };

      localStorage.setItem(getUserCacheKey(userData.uid), JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error saving cached user data:', error);
    }
  }

  // ======= Update Methods =======

  /**
   * Update a single field in the user document
   */
  async updateField(fieldPath, value) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');

    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      [fieldPath]: value
    });
  }

  /**
   * Update display name
   */
  async updateDisplayName(displayName) {
    const trimmed = (displayName || '').trim();
    await this.updateField('displayName', trimmed);
  }

  /**
   * Update primary email address
   */
  async updateSendEmailAddress(email) {
    const trimmed = (email || '').trim();
    
    if (trimmed && !this._isValidEmail(trimmed)) {
      throw new Error('Invalid email format');
    }
    
    // If empty, use auth email as fallback
    const finalEmail = trimmed || auth.currentUser?.email || '';
    await this.updateField('sendEmailAddress', finalEmail);
  }

  /**
   * Update secondary email addresses
   */
  async updateSecondaryEmails(emails) {
    const sanitized = (emails || [])
      .map(email => email.trim())
      .filter(email => email !== '' && this._isValidEmail(email));
    
    await this.updateField('secondarySendEmails', sanitized);
  }

  /**
   * Update About Me
   */
  async updateAboutMe(aboutMe) {
    const trimmed = (aboutMe || '').trim();
    await this.updateField('aboutMe', trimmed);
  }

  /**
   * Update hint keywords
   */
  async updateHintKeywords(keywords) {
    const sanitized = (keywords || [])
      .map(k => k.trim())
      .filter(k => k !== '');
    
    await this.updateField('hintKeywords', sanitized);
  }

  /**
   * Update summarization language
   */
  async updateUserLocale(locale) {
    const isValid = SUPPORTED_LOCALES.some(l => l.code === locale);
    if (!isValid) {
      throw new Error('Invalid locale');
    }
    await this.updateField('userLocale', locale);
  }

  /**
   * Update default summary type
   */
  async updateDefaultSummaryType(summaryType) {
    if (!SUMMARY_TYPES[summaryType]) {
      throw new Error('Invalid summary type');
    }
    await this.updateField('defaultSummaryType', summaryType);
  }

  /**
   * Update default send email setting
   */
  async updateDefaultSendEmail(enabled) {
    await this.updateField('defaultSendEmail', !!enabled);
  }

  /**
   * Update include transcript in email setting
   */
  async updateAddTranscriptInEmail(enabled) {
    await this.updateField('addTranscriptInEmail', !!enabled);
  }

  /**
   * Update webhook enabled status
   */
  async updateWebhookEnabled(enabled) {
    await this.updateField('webhook.enabled', !!enabled);
  }

  /**
   * Update webhook URL
   */
  async updateWebhookURL(url) {
    const trimmed = (url || '').trim();
    
    if (trimmed && !this._isValidURL(trimmed)) {
      throw new Error('Invalid webhook URL. Must be http or https.');
    }
    
    await this.updateField('webhook.principalWebhook', trimmed);
  }

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(imageFile) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');

    // Validate file
    if (!imageFile) throw new Error('No image file provided');
    if (imageFile.size > 1 * 1024 * 1024) throw new Error('Image must be less than 1MB');
    
    const storageRefPath = ref(storage, `userPictures/${uid}.jpg`);
    
    // Upload with JPEG content type
    await uploadBytes(storageRefPath, imageFile, {
      contentType: 'image/jpeg'
    });
    
    // Get and return the download URL
    const photoURL = await getDownloadURL(storageRefPath);
    return photoURL;
  }

  /**
   * Fetch webhook execution logs
   */
  async fetchWebhookLogs(limitCount = 50) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');

    const logsRef = collection(db, 'users', uid, 'logWebhooks');
    const logsQuery = query(logsRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(logsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  // ======= Validation Helpers =======

  _isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  }

  _isValidURL(url) {
    try {
      const urlObj = new URL(url.trim());
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }
}

export const userService = new UserService();
export { SignInProvider, FolderModel, UserContact };
